import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import Box from '@mui/material/Box';
import Header from './Header';
import Footer from './Footer';
import IntroPage from './IntroPage';
import SignUp from './SignUp';
import Dashboard from './Dashboard';
import Login from './Login';
import { UserProvider } from './UserContext';
import { TiersProvider } from './TiersContext';
import ProtectedRoute from './ProtectedRoute';
import About from './About';
import Contests from './Contests';
import Pricing from './Pricing';
import Contact from './Contact';
import AcceptableUse from './AcceptableUse';
import Privacy from './Privacy';
import Terms from './Terms';
import ClubForm from './ClubForm';
import ContestForm from './ContestForm'; // Renamed from CreateContest
import ContestPage from './ContestPage';
import MyClubIsOnSnapperish from './MyClubIsOnSnapperish';
import SettingsPage from './SettingsPage';
import SnappyPage from './SnappyPage';
import EmailContestantsPage from './EmailContestantsPage';
import MonitorPage from './MonitorPage';
import JoinClub from './JoinClub';
import FAQ from './FAQ';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  // State to track dark/light mode
  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Listen for dark/light mode changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setIsDarkMode(e.matches);
    };

    // Add listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Create theme based on dark/light mode
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
    components: {
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            zIndex: 1,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TiersProvider>
      <UserProvider>
        <Router>
          <ScrollToTop />
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}>
            <Header />

            <Box sx={{ flex: 1 }}>
              <Routes>
                {/* Public routes - no authentication required */}
                <Route path="/" element={<IntroPage />} />
                <Route path="/signup" element={process.env.REACT_APP_COMING_SOON === 'true' ? <Navigate to="/" replace /> : <SignUp />} />
                <Route path="/login" element={process.env.REACT_APP_COMING_SOON === 'true' && !new URLSearchParams(window.location.search).has('code') ? <Navigate to="/" replace /> : <Login />} />
                <Route path="/about" element={<About />} />
                <Route path="/contests" element={<Contests />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/acceptableuse" element={<AcceptableUse />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/myclubisonsnapperish" element={<MyClubIsOnSnapperish />} />
                <Route path="/join" element={<JoinClub />} />
                <Route path="/join/:code" element={<JoinClub />} />
                <Route path="/faq" element={<FAQ />} />

                {/* Protected routes - authentication required */}
                <Route path="/dashboard" element={
                  <ProtectedRoute requireAuth={true}>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/createclub" element={
                  <ProtectedRoute requireAuth={true}>
                    <ClubForm />
                  </ProtectedRoute>
                } />
                {/* Club view/edit route */}
                <Route path="/club/:clubShortName" element={
                  <ProtectedRoute requireAuth={true}>
                    <ClubForm />
                  </ProtectedRoute>
                } />
                {/* Contest page */}
                <Route path="/club/:clubShortName/contest/:contestShortName" element={
                  <ProtectedRoute requireAuth={true}>
                    <ContestPage />
                  </ProtectedRoute>
                } />
                <Route path="/club/:clubShortName/contest/:contestShortName/snappy" element={
                  <ProtectedRoute requireAuth={true}>
                    <SnappyPage />
                  </ProtectedRoute>
                } />
                {/* Create contest route */}
                <Route path="/club/:clubShortName/createcontest" element={
                  <ProtectedRoute requireAuth={true}>
                    <ContestForm />
                  </ProtectedRoute>
                } />
                {/* Create gallery route */}
                <Route path="/club/:clubShortName/creategallery" element={
                  <ProtectedRoute requireAuth={true}>
                    <ContestForm isGalleryMode={true} />
                  </ProtectedRoute>
                } />
                {/* Edit contest route */}
                <Route path="/club/:clubShortName/contest/:contestShortName/edit" element={
                  <ProtectedRoute requireAuth={true}>
                    <ContestForm />
                  </ProtectedRoute>
                } />
                <Route path="/club/:clubShortName/contest/:contestShortName/emailcontestants" element={
                  <ProtectedRoute requireAuth={true}>
                    <EmailContestantsPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute requireAuth={true}>
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/monitor" element={
                  <ProtectedRoute requireAuth={true}>
                    <MonitorPage />
                  </ProtectedRoute>
                } />

                {/* Example of role-based protection
                <Route path="/admin" element={
                  <ProtectedRoute requireAuth={true} requiredRole="admin">
                    <AdminPanel />
                  </ProtectedRoute>
                } />
                 */}
              </Routes>
            </Box>

            <Footer />
          </Box>
        </Router>
      </UserProvider>
      </TiersProvider>
    </ThemeProvider>
  );
}

export default App;